import Checkbox from 'components/forms/Checkbox';
import { CheckoutEvents } from 'libs/Events/constants';
import Events from 'libs/Events';
import Input from 'components/forms/Input';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Select from 'components/forms/Select';
import { TextLinkWysisygForceTargetBlank } from 'components/text/TextLink';
import ThemeButton from 'components/buttons/ThemeButton';
import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import appProp from 'utils/proptypes/application';
import { defaultShippingMethod } from 'views/Page/Checkout';
import { injectModels } from 'state';
import pageProps from 'utils/proptypes/page';
import styled from 'libs/styled';
import useBasket from 'hooks/useBasket';
import useFormSubmit from 'hooks/useFormSubmit';
import { useGiftCertificateContext } from 'libs/GiftCertificate/GiftCertificateContext';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const Wrapper = styled('form', {
    shouldForwardProp: prop => ['hide'].indexOf(prop) === -1,
})`
    display: ${({ hide }) => (hide ? 'none' : 'flex')};
    flex-direction: column;
    margin-top: 24px;
`;

const Box = styled('div')`
    margin: 16px 0;
`;

const StyledSelect = styled(Select)`
    margin: 16px 0;
`;

const StepButton = styled(ThemeButton)`
    width: 100%;
    margin-top: 24px;
    font-size: 16px;
`;

const AdyenForm = ({
    application,
    page,
    hide = false,
    scrollToShippingSection,
    showShippingAndPayment,
    setShowShippingAndPayment,
    tracking,
}) => {
    const { giftCertBasket } = useGiftCertificateContext();
    const basket = useBasket(giftCertBasket);

    const shippingButtonLabel = useSelector(state => state.page.data.checkout.address.proceed_button_label);
    const paymentMethodId = useSelector(state => state.basket?.paymentMethodId);
    const { t } = useTranslation();

    const userCountry = application.shop_config.user_country;
    const country = application.shop_config.countries.find(country => country.id === userCountry);
    const statesAvailable = country && country.states && Object.keys(country.states).length > 0;

    const onSuccessfulSubmit = async data => {
        try {
            scrollToShippingSection();
            setShowShippingAndPayment(true);

            // GTM Tracking
            if (tracking?.trigger) {
                Events.trigger(CheckoutEvents.SHIPPING, { option: defaultShippingMethod });
                if (paymentMethodId) {
                    Events.trigger(CheckoutEvents.PAYMENT, { option: paymentMethodId });
                }
                tracking.setTrigger(false);
            }

            delete data['purchaseTerms'];
            await basket.addBasketInformation({ address: data });
        } catch (error) {
            console.error(error);
        } finally {
            if (!statesAvailable && basket?.setAddressState) {
                basket.setAddressState('');
            }
        }
    };

    const { newsletter, purchase_terms } = page.data.checkout.address;
    // Only require the checkbox if the CMS has added purchase terms, otherwise checkout would be blocked with no way to accept
    const purchaseTermsRequired = Boolean(purchase_terms?.purchase_terms_text);

    const { error, selectedValueArray, handleSubmit } = useFormSubmit({
        action: onSuccessfulSubmit,
        ignore: ['newsletter'],
        required: purchaseTermsRequired ? ['purchaseTerms'] : [],
    });
    const selectedValue = selectedValueArray[0];
    const setSelectedValue = selectedValueArray[1];

    const [purchaseTermsAccepted, setPurchaseTermsAccepted] = useState(false);

    const handlePurchaseTerms = e => {
        const checked = e.target.checked;
        setPurchaseTermsAccepted(checked);
        if (!checked && showShippingAndPayment) {
            setShowShippingAndPayment(false);
        }
    };

    const getCheckboxError = (name, errorMessage) => {
        const e = error.find(err => err.name === name);
        if (!e) return undefined;
        if (name === 'purchaseTerms' && purchaseTermsAccepted) return undefined;
        return errorMessage ? { ...e, message: errorMessage } : e;
    };

    const handleNewsletter = e => {
        localStorage.setItem('newsletter_signup', e.target.checked);
        localStorage.setItem('newsletter_signup_list', 'default');
    };

    const handleChange = () => {
        // Hide the shipping and payment options after change to force a new /payment update
        if (showShippingAndPayment) {
            setShowShippingAndPayment(false);
        }
    };

    const stateOptions =
        statesAvailable &&
        Object.keys(country.states).map(state => {
            return {
                label: country.states[state],
                value: state,
            };
        });

    const fields = [
        {
            defaultValue: basket.address.first_name,
            label: t('checkout_basket.adyen.first_name'),
            name: 'firstName',
            required: true,
            type: 'input',
            handleChange,
        },
        {
            defaultValue: basket.address.last_name,
            label: t('checkout_basket.adyen.last_name'),
            name: 'lastName',
            required: true,
            type: 'input',
            handleChange,
        },
        {
            defaultValue: basket.address.address1,
            label: t('checkout_basket.adyen.address'),
            name: 'address1',
            required: true,
            type: 'input',
            handleChange,
        },
        {
            defaultValue: basket.address.zip,
            label: t('checkout_basket.adyen.zip_code'),
            name: 'zipCode',
            required: true,
            type: 'input',
            handleChange,
        },
        {
            defaultValue: basket.address.city,
            label: t('checkout_basket.adyen.city'),
            name: 'city',
            required: true,
            type: 'input',
            handleChange,
        },
        {
            // Select lists can not be required at the moment, that is why first stateOptions is used as "default"
            defaultValue: stateOptions && stateOptions[0] && stateOptions[0].label,
            label: t('checkout_basket.adyen.state'),
            name: 'state',
            options: stateOptions,
            required: true,
            type: 'select',
            handleChange,
        },
        {
            defaultValue: basket.address.email,
            label: t('checkout_basket.adyen.email'),
            name: 'email',
            required: true,
            type: 'email',
            handleChange,
        },
        {
            defaultValue: basket.address.phone,
            label: t('checkout_basket.adyen.phone_number'),
            name: 'phoneNumber',
            required: true,
            type: 'tel',
            handleChange,
        },
    ];

    if (purchase_terms?.purchase_terms_text) {
        fields.push({
            label: (
                <Wysiwyg
                    data={purchase_terms.purchase_terms_text}
                    tagComponents={{ url: TextLinkWysisygForceTargetBlank }}
                    textComponent={props => <span {...props} />}
                />
            ),
            name: 'purchaseTerms',
            required: true,
            type: 'checkbox',
            disableNativeRequired: true,
            errorMessage: purchase_terms.purchase_terms_error_message,
            handleChange: handlePurchaseTerms,
        });
    }

    if (newsletter?.active) {
        fields.push({
            label: (
                <Wysiwyg
                    data={newsletter?.text}
                    tagComponents={{ url: TextLinkWysisygForceTargetBlank }}
                    textComponent={props => <span {...props} />}
                />
            ),
            name: 'newsletter',
            type: 'checkbox',
            handleChange: handleNewsletter,
        });
    }

    return (
        <Wrapper hide={hide} onSubmit={handleSubmit}>
            {fields.map(({ name, label, type, options, active, defaultValue, required, handleChange, errorMessage, disableNativeRequired, disabled }) => {
                if (active === false) {
                    return null;
                }

                return type === 'select' ? (
                    statesAvailable ? (
                        <StyledSelect
                            isAdyenForm
                            defaultValue={statesAvailable ? defaultValue : ''}
                            error={error.find(e => e.name === name)}
                            handleChange={handleChange}
                            key={name}
                            label={label}
                            name={name}
                            options={options}
                            selectedValue={selectedValue}
                            setSelectedValue={setSelectedValue}
                        />
                    ) : null
                ) : type === 'checkbox' ? (
                    <Checkbox
                        disabled={disabled}
                        disableNativeRequired={disableNativeRequired}
                        error={getCheckboxError(name, errorMessage)}
                        handleChange={handleChange}
                        key={name}
                        label={label}
                        name={name}
                        required={required}
                        type={type}
                    />
                ) : (
                    <Box key={name}>
                        <Input
                            defaultValue={defaultValue}
                            error={error.find(e => e.name === name)}
                            handleChange={handleChange}
                            label={label}
                            name={name}
                            required={required}
                            type={type}
                        />
                    </Box>
                );
            })}

            <StepButton disabled={showShippingAndPayment} mt="24px" size="lg" theme="black" type="submit">
                {shippingButtonLabel}
            </StepButton>
        </Wrapper>
    );
};

AdyenForm.propTypes = {
    application: appProp.isRequired,
    hide: PropTypes.bool,
    page: pageProps.isRequired,
    scrollToShippingSection: PropTypes.func.isRequired,
    setShowShippingAndPayment: PropTypes.func.isRequired,
    showShippingAndPayment: PropTypes.bool,
    tracking: PropTypes.shape({
        trigger: PropTypes.bool,
        setTrigger: PropTypes.func,
    }),
};

export default injectModels(['application', 'page'])(AdyenForm);
